// HU-11 · Bandeja del Comité y apertura del caso (F-03, Sprint 2; rev. 16, 23 criterios).
//
// LA BANDEJA PRIORIZA, NO ESCONDE (RN-28). Con 14 países alimentando el mismo sistema, un
// listado plano es inservible: el miembro del Comité tendría que abrir uno por uno para
// saber cuál le toca. Esta pantalla convierte el listado en una jornada de trabajo:
//
//   1  Observados          «Subsanar observación»   con el motivo del aprobador (C5)
//   2  Vencidos            «Evaluar el caso»        texto + ícono, no solo color (C8)
//   3  Por vencer          «Evaluar el caso»
//   4  En plazo            «Evaluar el caso»        el más antiguo primero (C4)
//   5  Graves sin ampliar  «Ampliar informe»
//
// El orden y la clasificación vienen del servidor (`GET /evaluacion/bandeja`); aquí no se
// reordena ni se reclasifica nada. Los filtros viajan como query string y se combinan con
// Y (C17); al filtrar se conservan los bloques y desaparecen los vacíos (C18).
//
// DOS SALIDAS DE LA BANDEJA:
//   · abrir un caso lo muestra en SOLO LECTURA (C21): el expediente con el Formato Verde y
//     sus evidencias, sin un solo campo editable;
//   · «Ver todos los casos» (C20, C23) busca por número un caso que NO está en la bandeja
//     —pasó a seguimiento, se cerró— y lo abre igual. La bandeja no esconde: prioriza.
//
// SIN ÁMBITO POR PAÍS (RN-40, ILENDER 10-sep-2026): el filtro de país ofrece TODO el
// catálogo (C11) y el Comité ve los casos de todos los países (C12).

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { comoFechaDeInstante, comoFechaYHora } from '../lib/fechas';
import type { ReactNode } from 'react';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Clock, FileUp, FolderOpen, Inbox, PauseCircle, PenLine, RefreshCw,
  Search, Stethoscope, TimerOff, X,
  ClipboardList,
} from 'lucide-react';
import {
  Alert, Button, Card, Codigo, EmptyState, Input, Mono, PageHeader, Select, StatusBadge,
} from '../components/ui';
import type { Tono } from '../components/ui';
import Bandera from '../components/Banderas';
import SelectorDePais from '../components/ui/SelectorDePais';
import FiltrosColapsables from '../components/ui/FiltrosColapsables';
import Paginacion, { POR_PAGINA_DE_LISTADOS, paginar, paginasDe } from '../components/ui/Paginacion';
import { buscarEnTodosLosCasos, consultarBandeja } from '../api/bandeja';
import type {
  BandejaDelComite as Bandeja, BusquedaEnTodosLosCasos, ClaveDeBloque, EstadoDelPlazo, FilaDeBandeja, FiltrosDeBandeja,
} from '../api/bandeja';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { etiquetaDelEstadoDelCaso } from '../lib/etiquetas';
import { MENSAJE_RANGO_INVERTIDO, rangoInvertido } from '../lib/entradas';
import { filtrosIniciales, guardarFiltros } from '../lib/filtrosPersistidos';
import RangoDeFechas from '../components/ui/RangoDeFechas';
import { useDiferido } from '../lib/useDiferido';
import { useZonaDelPais } from '../lib/CatalogosContexto';
import type { PestanaDeLaEvaluacion } from './EvaluacionDelCaso';

// DD/MM/AAAA EN TODA LA INTERFAZ (25-sep-2026). Antes recortaba el ISO y lo mostraba
// tal cual, asi que en la misma fila convivian «2026-09-24» y «25/09/26». RN-82: el día se
// toma en la zona del país del caso.
const fecha = (iso: unknown, zona: string | null): string => comoFechaDeInstante(iso, zona) || '—';

/**
 * C8 · RNF-11: el estado del plazo se señala con TEXTO e ÍCONO, no solo con color. Un
 * daltónico —o una impresión en blanco y negro— tiene que leer «Plazo interno vencido».
 */
const PLAZO: Record<EstadoDelPlazo, { tone: Tono; icon: typeof Clock }> = {
  vencido: { tone: 'danger', icon: AlertTriangle },
  por_vencer: { tone: 'warning', icon: Clock },
  en_plazo: { tone: 'success', icon: CheckCircle2 },
  detenido: { tone: 'neutral', icon: PauseCircle },
};

/**
 * HU-11-C13: el filtro por estado del plazo SE NOMBRA COMO EL BLOQUE al que lleva —«Vencidos»,
 * «Por vencer», «En plazo»—, no como el texto de la columna («Plazo interno vencido»): es lo
 * que el Comité ve arriba y lo que elige aquí. Los `value` son los que espera el servidor
 * (`estado_plazo`). `detenido` es el reloj parado de los otros dos bloques —Observados y
 * Graves sin ampliar—, y se ofrece con esos nombres para que también se lleguen desde aquí.
 */
const ESTADOS_DE_PLAZO_PARA_FILTRAR: { value: EstadoDelPlazo; label: string }[] = [
  { value: 'vencido', label: 'Vencidos' },
  { value: 'por_vencer', label: 'Por vencer' },
  { value: 'en_plazo', label: 'En plazo' },
  { value: 'detenido', label: 'Reloj detenido · Observados y Graves sin ampliar' },
];

/**
 * LA ACCIÓN DE LA FILA ABRE LA PESTAÑA QUE LA RESUELVE (HU-11-C5). El observado va al
 * informe; el grave sin ampliar, a la actividad 8; el resto, a la investigación. El verbo
 * del botón es corto —la columna «Acción pendiente» ya dice la frase completa—.
 */
const ACCION_POR_BLOQUE: Record<ClaveDeBloque, { verbo: string; pestana: PestanaDeLaEvaluacion; icon: typeof Stethoscope }> = {
  observados: { verbo: 'Subsanar', pestana: 'informe', icon: PenLine },
  graves_sin_ampliar: { verbo: 'Ampliar', pestana: 'ampliacion', icon: FileUp },
  vencidos: { verbo: 'Evaluar', pestana: 'investigacion', icon: Stethoscope },
  por_vencer: { verbo: 'Evaluar', pestana: 'investigacion', icon: Stethoscope },
  en_plazo: { verbo: 'Evaluar', pestana: 'investigacion', icon: Stethoscope },
};
const ACCION_POR_DEFECTO = ACCION_POR_BLOQUE.en_plazo;

/** C6 · C7: la gravedad decidida por el Comité. Sin decisión, la celda va VACÍA. */
function Gravedad({ valor }: { valor: 'grave' | 'no_grave' | null }) {
  if (valor === 'grave') return <StatusBadge tone="danger">Grave</StatusBadge>;
  if (valor === 'no_grave') return <StatusBadge tone="neutral">No grave</StatusBadge>;
  return <span className="text-ink-soft" role="img" aria-label="Gravedad sin decidir">—</span>;
}

function EstadoDelPlazoInterno({ fila, zona }: { fila: FilaDeBandeja; zona: string | null }) {
  const { tone, icon } = PLAZO[fila.estado_del_plazo] ?? PLAZO.detenido;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <StatusBadge tone={tone} icon={icon}>{fila.texto_del_plazo}</StatusBadge>
      {fila.vencimiento_interno && fila.estado_del_plazo !== 'detenido' && (
        <span className="text-meta text-ink-soft pl-1">
          vence {comoFechaYHora(fila.vencimiento_interno, zona)}
        </span>
      )}
      {!fila.plazo_fiable && (
        <span className="text-meta text-amber-700 pl-1">reloj sin zona horaria configurada</span>
      )}
    </span>
  );
}

/** C6: las siete columnas. C5: el observado muestra además el motivo. */
// `memo`: con cientos de filas, cada tecla en un filtro re-pintaba la tabla entera
// mientras llegaba la respuesta; las filas solo cambian cuando cambia la bandeja.
const FilaDelBloque = memo(function FilaDelBloque({ fila, clave, onAbrir, onEvaluar }: {
  fila: FilaDeBandeja; clave: ClaveDeBloque; onAbrir: (casoId: string) => void;
  onEvaluar?: (casoId: string, pestana: PestanaDeLaEvaluacion) => void;
}) {
  const zona = useZonaDelPais()(fila.pais);
  const accion = ACCION_POR_BLOQUE[clave] ?? ACCION_POR_DEFECTO;
  // HU-17-C8 / HU-18 (`ui/MG-ILE-44.md`): la fila del bloque «Observados» y su motivo llevan
  // el testid de la spec; el motivo es `motivo_de_observacion` del servidor, el mismo hecho
  // que la pestaña «Informe FAR-03» muestra como motivo vigente.
  const observado = clave === 'observados';
  return (
    <tr data-testid={observado ? 'comite-bandeja-observados-fila' : undefined} className="border-t border-hairline hover:bg-slate-50">
      <td className="px-4 py-2.5 align-top">
        <Codigo className="whitespace-nowrap">{fila.numero ?? fila.caso_id.slice(0, 8)}</Codigo>
        <div className="text-meta text-ink-soft mt-0.5">{etiquetaDelEstadoDelCaso(fila.estado_del_caso)}</div>
      </td>
      <td className="px-4 py-2.5 align-top">
        <span className="inline-flex items-center gap-2 text-compacto">
          <Bandera cod={fila.pais} nombre={fila.nombre_pais} />
          {fila.nombre_pais}
        </span>
      </td>
      <td className="px-4 py-2.5 align-top">{fila.producto ?? <span className="text-ink-soft">—</span>}</td>
      <td className="px-4 py-2.5 align-top whitespace-nowrap"><Mono className="text-meta">{fecha(fila.fecha_recepcion, zona)}</Mono></td>
      <td className="px-4 py-2.5 align-top"><EstadoDelPlazoInterno fila={fila} zona={zona} /></td>
      <td className="px-4 py-2.5 align-top">
        <span className="text-compacto font-medium text-ink">{fila.accion_pendiente}</span>
        {fila.motivo_de_observacion && (
          <p data-testid={observado ? 'comite-bandeja-observados-fila-motivo' : undefined}
            className="text-meta text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1 max-w-xs">
            {fila.motivo_de_observacion}
          </p>
        )}
      </td>
      <td className="px-4 py-2.5 align-top"><Gravedad valor={fila.gravedad} /></td>
      <td className="px-4 py-2.5 align-top text-right whitespace-nowrap">
        {/* HU-12/15/43: la accion pendiente se resuelve en la evaluacion, en SU pestaña. */}
        {onEvaluar && (
          <Button size="sm" variant="primary" icon={accion.icon} onClick={() => onEvaluar(fila.caso_id, accion.pestana)}
            aria-label={`Evaluar ${fila.numero ?? 'el caso'}`} title={fila.accion_pendiente} className="mr-2">
            {accion.verbo}
          </Button>
        )}
        <Button size="sm" variant="secondary" icon={FolderOpen} onClick={() => onAbrir(fila.caso_id)}
          aria-label={`Abrir ${fila.numero ?? 'el caso'} en solo lectura`}>
          Abrir
        </Button>
      </td>
    </tr>
  );
});

const COLUMNAS = ['N.º de caso', 'País', 'Producto', 'Recibido', 'Plazo interno', 'Acción pendiente', 'Gravedad'];

/**
 * C2: un bloque con su encabezado «Vencidos (3)». Solo se pinta si tiene filas (C18).
 *
 * DE 10 EN 10 POR BLOQUE (operador, 5-oct-2026: «los listados paginan de 10 en 10»). El
 * encabezado sigue contando el bloque entero; la tabla pinta solo la página y, si el bloque
 * tiene más de 10, lleva su propio pie. El orden es el del servidor: aquí solo se trocea.
 */
function Bloque({ bloque, pagina: paginaPedida, onPagina, onAbrir, onEvaluar }: {
  bloque: Bandeja['bloques'][number]; pagina: number; onPagina: (clave: ClaveDeBloque, pagina: number) => void;
  onAbrir: (casoId: string) => void;
  onEvaluar?: (casoId: string, pestana: PestanaDeLaEvaluacion) => void;
}) {
  const total = bloque.filas.length;
  const pagina = Math.min(Math.max(1, paginaPedida), paginasDe(total));
  const filas = paginar(bloque.filas, pagina);
  return (
    <Card pad={false} className="mb-4" title={`${bloque.titulo} (${bloque.cuenta})`}>
      {/* La seccion con nombre ES la zona que desplaza: al 200 % la tabla desborda y una zona
          desplazable sin nombre no se anuncia (WCAG 1.4.10 y 2.1.1, auditoria del 2-oct-2026).
          `relative`: recorta lo `absolute` de sus celdas (un `sr-only`), que si no agranda la
          pagina (BUG-1099). */}
      <section aria-label={`${bloque.titulo} (${bloque.cuenta})`} className="relative overflow-x-auto max-h-[70vh]">
        <div>
          <table className="w-full text-compacto">
            {/* `sticky`: con decenas de filas, las cabeceras acompañan al desplazamiento. */}
            <thead className="bg-canvas sticky top-0 z-[1]">
              <tr>
                {COLUMNAS.map((c) => (
                  <th key={c} scope="col"
                    className="font-semibold text-meta text-ink-soft px-4 py-2.5 whitespace-nowrap text-left">
                    {c}
                  </th>
                ))}
                <th scope="col" className="px-4 py-2.5"><span className="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => <FilaDelBloque key={f.caso_id} fila={f} clave={bloque.clave} onAbrir={onAbrir} onEvaluar={onEvaluar} />)}
            </tbody>
          </table>
        </div>
      </section>
      {total > POR_PAGINA_DE_LISTADOS && (
        <div className="px-4 py-3 border-t border-hairline">
          <Paginacion pagina={pagina} total={total} enEstaPagina={filas.length}
            onPagina={(p) => onPagina(bloque.clave, p)} etiqueta={`Paginación de ${bloque.titulo}`}
            sustantivo={['caso', 'casos']} data-testid={`comite-bloque-${bloque.clave}-paginacion`} />
        </div>
      )}
    </Card>
  );
}

/**
 * C20 · «Ver todos los casos». Un caso que ya no está en la bandeja sigue siendo
 * alcanzable por su número y se abre en solo lectura. Al volver, la bandeja conserva
 * el orden y los filtros: esta vista se monta ENCIMA, sin desmontar la bandeja.
 */
// Estados en los que todavia no hay evaluacion (el Comite no abrio el caso): sin «Ver evaluación».
const SIN_EVALUACION = new Set(['borrador', 'registrado']);

function TodosLosCasos({ onAbrir, onEvaluar, onVolver }: Readonly<{
  onAbrir: (casoId: string) => void;
  /** C1/C2 (22-sep-2026): la evaluación de un caso que ya salió del Comité se consulta en solo lectura
   *  (con la act. 8 y el estado de las acciones abiertos si el servidor lo dice). */
  onEvaluar?: (casoId: string, pestana: PestanaDeLaEvaluacion) => void;
  onVolver: () => void;
}>) {
  const [numero, setNumero] = useState('');
  const [resultado, setResultado] = useState<BusquedaEnTodosLosCasos | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [buscando, setBuscando] = useState(false);
  // Los resultados (hasta el tope del servidor) se ven de 10 en 10 (operador, 5-oct-2026).
  const [paginaDeResultados, setPaginaDeResultados] = useState(1);

  const buscar = async () => {
    if (numero.trim().length < 2) return;
    setBuscando(true);
    setFallo(null);
    try {
      setPaginaDeResultados(1);
      setResultado(await buscarEnTodosLosCasos(numero));
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setBuscando(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Comité · Todos los casos"
        hu="HU-11 · C20"
        title="Todos los casos"
        subtitle="Busca por número un caso que ya no está en la bandeja (en seguimiento, cerrado…) y ábrelo en solo lectura."
        icon={Search}
        actions={<Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>Volver a la bandeja</Button>}
      />
      <Card>
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); buscar(); }}>
          <label className="flex flex-col gap-1 text-meta font-semibold text-ink-soft w-full sm:w-64">
            Número de caso
            <Input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="PE-003-2026" />
          </label>
          <Button type="submit" icon={Search} disabled={buscando || numero.trim().length < 2}>
            {buscando ? 'Buscando…' : 'Buscar'}
          </Button>
        </form>
        {fallo && <div className="mt-4"><Alert tone="danger" title="No se pudo buscar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
        {resultado && resultado.casos.length === 0 && (
          <p className="mt-4 text-meta text-ink-soft">Ningún caso lleva ese número.</p>
        )}
        {/* El servidor corta en `tope` coincidencias; si quedaron otras fuera, se dice. */}
        {resultado?.hay_mas && (
          <div className="mt-4">
            <Alert tone="warning" icon={AlertTriangle} data-testid="comite-todos-hay-mas">
              Se muestran los primeros <Mono className="text-ink">{resultado.tope}</Mono> resultados.
              Escribe más del número para acotar la búsqueda.
            </Alert>
          </div>
        )}
        {resultado && resultado.casos.length > 0 && (
          <ul className="mt-4 divide-y divide-hairline">
            {paginar(resultado.casos, paginaDeResultados).map((c) => (
              <li key={c.caso_id} className="flex flex-wrap items-center gap-3 py-3">
                <Codigo className="whitespace-nowrap">{c.numero}</Codigo>
                <span className="inline-flex items-center gap-1.5 text-compacto"><Bandera cod={c.pais} nombre={c.nombre_pais} />{c.nombre_pais}</span>
                <span className="text-compacto">{c.producto ?? '—'}</span>
                <StatusBadge tone="info">{etiquetaDelEstadoDelCaso(c.estado_del_caso)}</StatusBadge>
                <Gravedad valor={c.gravedad} />
                <span className="ml-auto inline-flex flex-wrap gap-2">
                  {onEvaluar && !SIN_EVALUACION.has(c.estado_del_caso) && (
                    <Button size="sm" variant="secondary" icon={ClipboardList} onClick={() => onEvaluar(c.caso_id, 'investigacion')}
                      aria-label={`Ver evaluación de ${c.numero}`}>
                      Ver evaluación
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" icon={FolderOpen} onClick={() => onAbrir(c.caso_id)}>
                    Abrir en solo lectura
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
        {resultado && resultado.casos.length > POR_PAGINA_DE_LISTADOS && (
          <div className="mt-2">
            <Paginacion pagina={paginaDeResultados} total={resultado.casos.length}
              enEstaPagina={paginar(resultado.casos, paginaDeResultados).length} onPagina={setPaginaDeResultados}
              etiqueta="Paginación de los resultados" sustantivo={['resultado', 'resultados']}
              data-testid="comite-todos-paginacion" />
          </div>
        )}
      </Card>
    </>
  );
}

const SIN_FILTROS: FiltrosDeBandeja = { pais: '', estado_plazo: '', gravedad: '', producto: '', desde: '', hasta: '', numero: '' };

export default function BandejaDelComite({ onAbrirCaso, onEvaluarCaso }: {
  /** C21: abre el caso en SOLO LECTURA (el expediente con el Formato Verde y sus evidencias). */
  onAbrirCaso: (casoId: string) => void;
  /** HU-12/15/43: abre la evaluacion del caso en la pestaña que resuelve la acción pendiente. */
  onEvaluarCaso?: (casoId: string, pestana: PestanaDeLaEvaluacion) => void;
}) {
  // LOS FILTROS SOBREVIVEN A ABRIR UN CASO Y VOLVER (bug 628 · CP-11-02 p.10). «Abrir»
  // cambia de módulo: `App` monta el expediente y esta pantalla se desmonta entera, así que
  // con `useState(SIN_FILTROS)` la bandeja volvía en blanco y había que rehacer el filtro
  // caso por caso. Se recuperan de `sessionStorage` y se olvidan al cerrar sesión: ver
  // `lib/filtrosPersistidos.ts`.
  const [filtros, setFiltros] = useState<FiltrosDeBandeja>(() => filtrosIniciales('comite', SIN_FILTROS));
  const [bandeja, setBandeja] = useState<Bandeja | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [viendoTodos, setViendoTodos] = useState(false);
  // La página de cada bloque, por su clave. Vive aquí y no en el bloque para que cada carga
  // de la bandeja (un filtro nuevo, «Refrescar») la devuelva a la primera de un solo golpe.
  const [paginas, setPaginas] = useState<Partial<Record<ClaveDeBloque, number>>>({});
  const irAPagina = useCallback((clave: ClaveDeBloque, pagina: number) => {
    setPaginas((p) => ({ ...p, [clave]: pagina }));
  }, []);

  // D3 · Los campos de TEXTO consultan 300 ms después de la última tecla; los
  // desplegables y las fechas, al instante.
  const productoDiferido = useDiferido(filtros.producto ?? '');
  const numeroDiferido = useDiferido(filtros.numero ?? '');
  // Campo a campo en las dependencias: si dependiera del objeto `filtros`, cada tecla
  // en «Producto» lo re-crearía y la consulta saldría igual, con el texto viejo.
  const { pais, estado_plazo: estadoPlazo, gravedad, desde, hasta } = filtros;
  const filtrosEfectivos = useMemo<FiltrosDeBandeja>(
    () => ({ pais, estado_plazo: estadoPlazo, gravedad, desde, hasta, producto: productoDiferido, numero: numeroDiferido }),
    [pais, estadoPlazo, gravedad, desde, hasta, productoDiferido, numeroDiferido],
  );

  // D3 · TOKEN DE SECUENCIA: cada consulta lleva su número y solo la ÚLTIMA lanzada
  // puede escribir en pantalla. Sin esto, la respuesta lenta de la bandeja completa
  // llegaba después que la del filtro y la pisaba (observado en la E2E del 12-sep-2026).
  // Un rango de fechas al revés no se consulta: se dice bajo el campo y se espera a que
  // lo corrijan. Consultarlo devolvía una bandeja vacía sin explicar por qué.
  const fechasInvertidas = rangoInvertido(filtrosEfectivos.desde, filtrosEfectivos.hasta);
  const secuencia = useRef(0);
  const cargar = useCallback(async () => {
    if (fechasInvertidas) return;
    const mia = ++secuencia.current;
    setCargando(true);
    setFallo(null);
    try {
      const respuesta = await consultarBandeja(filtrosEfectivos);
      if (mia !== secuencia.current) return;
      setBandeja(respuesta);
      setPaginas({});
    } catch (e) {
      if (mia !== secuencia.current) return;
      setFallo(aFallo(e));
    } finally {
      if (mia === secuencia.current) setCargando(false);
    }
  }, [filtrosEfectivos, fechasInvertidas]);

  useEffect(() => { cargar(); }, [cargar]);

  // Se guarda lo QUE SE VE en los campos —no los diferidos—: al volver, la pantalla tiene
  // que aparecer tal cual se dejó. Quitar los filtros también se guarda, y es lo correcto:
  // si se quitaron a propósito, volver no debe resucitarlos.
  useEffect(() => { guardarFiltros('comite', filtros); }, [filtros]);

  const hayFiltro = useMemo(() => Object.values(filtros).some(Boolean), [filtros]);
  // Cuántos filtros hay puestos, para la tarjeta plegada: el rango «Recibido» cuenta como uno
  // aunque tenga desde y hasta; el buscador por número cuenta si tiene texto.
  const filtrosActivos = useMemo(() => (
    [filtros.pais, filtros.estado_plazo, filtros.gravedad, filtros.producto, filtros.numero].filter(Boolean).length
    + (filtros.desde || filtros.hasta ? 1 : 0)
  ), [filtros]);
  const fijar = (parte: Partial<FiltrosDeBandeja>) => setFiltros((f) => ({ ...f, ...parte }));

  // C20: la vista de «todos los casos» se monta encima; el estado de la bandeja (filtros y
  // datos) sigue vivo debajo y al volver está tal cual.
  if (viendoTodos) {
    return <TodosLosCasos onAbrir={onAbrirCaso} onEvaluar={onEvaluarCaso} onVolver={() => setViendoTodos(false)} />;
  }

  const encabezado: ReactNode = bandeja && !cargando && (
    <p className="text-meta text-ink-soft" aria-live="polite">
      <Mono className="text-ink">{bandeja.total}</Mono> {bandeja.total === 1 ? 'caso' : 'casos'}
      {bandeja.filtrado_por && <> · filtrado por: <span className="text-ink">{bandeja.filtrado_por}</span></>}
      {' · '}plazo interno {bandeja.plazo_interno_horas_habiles} h hábiles
    </p>
  );

  return (
    <>
      <PageHeader
        eyebrow="Comité · Bandeja"
        hu="HU-11 · RN-19 / RN-27 / RN-28 / RN-40"
        title="Evaluación del Comité"
        subtitle="Lo que le toca al Comité, en el orden en que hay que atenderlo. Cada fila dice qué falta hacer."
        icon={Stethoscope}
        actions={
          <>
            <Button variant="secondary" size="sm" icon={Search} onClick={() => setViendoTodos(true)}>
              Ver todos los casos
            </Button>
            <Button variant="secondary" size="sm" icon={RefreshCw} onClick={() => { cargar(); }} disabled={cargando}>
              Refrescar
            </Button>
          </>
        }
      />

      {/* C11 – C19: los filtros. Se combinan con Y; el país ofrece el catálogo entero (RN-40).
          Plegables y abiertos al entrar (operador, 5-oct-2026). */}
      <FiltrosColapsables className="mb-4" data-testid="comite-filtros" activos={filtrosActivos} actions={hayFiltro && (
        <Button size="sm" variant="ghost" icon={X} onClick={() => setFiltros(SIN_FILTROS)}>Quitar filtros</Button>
      )}>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,16rem),1fr))] gap-3">
          {/* Cada país con su bandera (operador, 5-oct-2026). */}
          <SelectorDePais
            aria-label="Filtrar por país"
            placeholder="Todos los países"
            value={filtros.pais ?? ''}
            onChange={(e) => fijar({ pais: String(e.target.value ?? '') })}
            paises={bandeja?.paises ?? []}
          />
          <Select
            aria-label="Filtrar por estado del plazo"
            placeholder="Cualquier estado del plazo"
            value={filtros.estado_plazo ?? ''}
            onChange={(e) => fijar({ estado_plazo: String(e.target.value ?? '') as EstadoDelPlazo | '' })}
            options={ESTADOS_DE_PLAZO_PARA_FILTRAR}
          />
          <Select
            aria-label="Filtrar por gravedad"
            placeholder="Cualquier gravedad"
            value={filtros.gravedad ?? ''}
            onChange={(e) => fijar({ gravedad: String(e.target.value ?? '') as 'grave' | 'no_grave' | '' })}
            options={[{ value: 'grave', label: 'Grave' }, { value: 'no_grave', label: 'No grave' }]}
          />
          <Input aria-label="Filtrar por producto" placeholder="Producto" value={filtros.producto ?? ''}
            onChange={(e) => fijar({ producto: e.target.value })} />
          {/* Un solo calendario de rango para «Recibido» (operador, 5-oct-2026): antes eran dos
              fechas sueltas sin «desde» ni «hasta» a la vista. */}
          <RangoDeFechas aria-label="Recibido" prefijo="Recibido" placeholder="Recibido: cualquier fecha" data-testid="comite-filtro-recibido"
            desde={filtros.desde ?? ''} hasta={filtros.hasta ?? ''} aria-invalid={fechasInvertidas || undefined}
            aria-describedby={fechasInvertidas ? 'rango-de-fechas-comite' : undefined}
            onCambio={({ desde, hasta }) => fijar({ desde, hasta })} />
        </div>
        {fechasInvertidas && <p id="rango-de-fechas-comite" role="alert" className="mt-1 text-meta text-rose-700">{MENSAJE_RANGO_INVERTIDO}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Input aria-label="Buscar por número de caso" placeholder="Buscar por N.º de caso (PE-002-2026)"
            className="!w-80 max-w-full" value={filtros.numero ?? ''} onChange={(e) => fijar({ numero: e.target.value })} />
          {encabezado}
        </div>
      </FiltrosColapsables>

      {fallo && (
        <Alert tone="danger" title="No se pudo cargar la bandeja" detalleTecnico={fallo.detalleTecnico}>
          {fallo.message}
          {fallo.estado === 0 && ' Revisa tu conexión e intenta refrescar.'}
        </Alert>
      )}

      {cargando && !bandeja && <p className="p-6 text-body text-ink-soft">Cargando la bandeja…</p>}

      {/* EL RECORTE SE DICE, NO SE CALLA (24-sep-2026, HU-31).
          La consulta tiene un tope —una válvula contra una consulta desbocada, no una
          regla de negocio— y ordena por fecha de recepción ascendente. Si el tope se
          alcanza, lo que queda fuera son los casos MÁS RECIENTES. Antes desaparecían de
          esta pantalla sin que nada fallara; ahora el servidor manda cuántos son.
          En la operación normal este aviso no existe. */}
      {!!bandeja?.candidatos_fuera_del_tope && (
        <div className="mb-3">
          <Alert tone="warning" title="La bandeja no está completa" icon={AlertTriangle}>
            Hay <Mono className="text-ink">{bandeja.candidatos_fuera_del_tope}</Mono>{' '}
            {bandeja.candidatos_fuera_del_tope === 1 ? 'caso que no entra' : 'casos que no entran'} en
            esta consulta, y son los de recepción más reciente. Usa los filtros para acotar la búsqueda.
          </Alert>
        </div>
      )}

      {/* C2, C18: los bloques NO VACÍOS, en el orden que manda el servidor. */}
      {bandeja?.bloques.map((b) => (
        <Bloque key={b.clave} bloque={b} pagina={paginas[b.clave] ?? 1} onPagina={irAPagina}
          onAbrir={onAbrirCaso} onEvaluar={onEvaluarCaso} />
      ))}

      {/* C23: sin nada pendiente NO hay tabla vacía: se explica y se ofrece el resto. */}
      {bandeja && bandeja.total === 0 && !fallo && (
        <Card pad={false}>
          <EmptyState
            icon={hayFiltro ? TimerOff : Inbox}
            title={bandeja.mensaje_de_vacia ?? 'No tienes casos pendientes de evaluación'}
            description={hayFiltro
              ? 'Prueba con otro filtro o quítalos todos.'
              : 'El resto de los casos sigue disponible desde «Ver todos los casos».'}
            action={hayFiltro
              ? <Button variant="secondary" icon={X} onClick={() => setFiltros(SIN_FILTROS)}>Quitar filtros</Button>
              : <Button variant="secondary" icon={Search} onClick={() => setViendoTodos(true)}>Ver todos los casos</Button>}
          />
        </Card>
      )}
    </>
  );
}
